import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import DatePicker from "@/components/site/DatePicker";

const SUPPLIERS = [
  { code: "all",     label: "All Suppliers" },
  { code: "amadeus", label: "Amadeus (GDS/NDC/LTC/PYTON/EAC)" },
  { code: "ndcx",    label: "NDC-X (Direct NDC)" },
];

const DOW = [
  { v: 1, label: "Mon" }, { v: 2, label: "Tue" }, { v: 3, label: "Wed" },
  { v: 4, label: "Thu" }, { v: 5, label: "Fri" }, { v: 6, label: "Sat" }, { v: 7, label: "Sun" },
];

/** Per-pax amount picker (type=percent|flat, value=number) */
export function PaxAmountField({ id, label, pax, onChange }) {
  const p = pax || { type: "percent", value: 0 };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</Label>
      <div className="grid grid-cols-5 gap-1.5">
        <Select value={p.type} onValueChange={(v) => onChange({ ...p, type: v })}>
          <SelectTrigger data-testid={`${id}-type`} className="h-10 col-span-2"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="percent">% Percent</SelectItem>
            <SelectItem value="flat">$ Flat</SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid={`${id}-value`}
          className="h-10 col-span-3 font-mono"
          type="number" step="0.01" min="0"
          value={p.value}
          onChange={(e) => onChange({ ...p, value: Number(e.target.value) || 0 })}
        />
      </div>
    </div>
  );
}

/** Common scope card (supplier, airline, optionally rbd/brand/flight/date/time/dow). */
export function ScopeFields({ rule, set, advanced = true, idPrefix }) {
  const toggleDow = (v) => {
    const cur = rule.travel_days_of_week || [];
    set({ travel_days_of_week: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Supplier</Label>
          <Select value={rule.supplier || "all"} onValueChange={(v) => set({ supplier: v })}>
            <SelectTrigger data-testid={`${idPrefix}-supplier`} className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>{SUPPLIERS.map((s) => (
              <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
            ))}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Airline (IATA code, or &quot;all&quot;)</Label>
          <Input
            data-testid={`${idPrefix}-airline`}
            className="h-10 uppercase"
            placeholder='e.g. EK, MS, "all"'
            value={(rule.airline || "all")}
            onChange={(e) => set({ airline: (e.target.value || "all").toUpperCase().slice(0, 6) })}
          />
        </div>
      </div>

      {advanced && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>RBD (comma-separated)</Label>
              <Input
                data-testid={`${idPrefix}-rbd`}
                className="h-10 uppercase font-mono"
                placeholder="Y, B, M  (empty = any)"
                value={(rule.rbd || []).join(", ")}
                onChange={(e) =>
                  set({ rbd: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) })
                }
              />
            </div>
            <div>
              <Label>Brand (comma-separated)</Label>
              <Input
                data-testid={`${idPrefix}-brand`}
                className="h-10 uppercase font-mono"
                placeholder="LIGHT, FLEX  (empty = any)"
                value={(rule.brand || []).join(", ")}
                onChange={(e) =>
                  set({ brand: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) })
                }
              />
            </div>
            <div>
              <Label>Flight number (contains)</Label>
              <Input
                data-testid={`${idPrefix}-flight-number`}
                className="h-10 uppercase font-mono"
                placeholder="EK202 (empty = any)"
                value={rule.flight_number || ""}
                onChange={(e) => set({ flight_number: e.target.value.toUpperCase() })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Travel date from</Label>
              <DatePicker
                testId={`${idPrefix}-date-from`}
                value={rule.travel_date_from || ""}
                onChange={(v) => set({ travel_date_from: v })}
                yearDropdown
                fromYear={new Date().getFullYear() - 1}
                toYear={new Date().getFullYear() + 5}
              />
            </div>
            <div>
              <Label>Travel date to</Label>
              <DatePicker
                testId={`${idPrefix}-date-to`}
                value={rule.travel_date_to || ""}
                onChange={(v) => set({ travel_date_to: v })}
                minDate={rule.travel_date_from || ""}
                yearDropdown
                fromYear={new Date().getFullYear() - 1}
                toYear={new Date().getFullYear() + 5}
              />
            </div>
            <div>
              <Label>Depart time from</Label>
              <Input
                data-testid={`${idPrefix}-time-from`}
                type="time" className="h-11"
                value={rule.travel_time_from || ""}
                onChange={(e) => set({ travel_time_from: e.target.value })}
              />
            </div>
            <div>
              <Label>Depart time to</Label>
              <Input
                data-testid={`${idPrefix}-time-to`}
                type="time" className="h-11"
                value={rule.travel_time_to || ""}
                onChange={(e) => set({ travel_time_to: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label className="block mb-1.5">Days of week (empty = any)</Label>
            <div className="flex flex-wrap gap-1.5">
              {DOW.map((d) => {
                const active = (rule.travel_days_of_week || []).includes(d.v);
                return (
                  <button
                    type="button"
                    key={d.v}
                    data-testid={`${idPrefix}-dow-${d.v}`}
                    onClick={() => toggleDow(d.v)}
                    className={
                      "px-3 h-9 rounded-full text-xs font-semibold border transition " +
                      (active
                        ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary-dark)]"
                        : "bg-white text-slate-500 border-slate-200 hover:border-[var(--brand-primary)]")
                    }
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className="flex items-center gap-3">
        <Switch
          data-testid={`${idPrefix}-per-segment`}
          checked={!!rule.per_segment}
          onCheckedChange={(v) => set({ per_segment: !!v })}
        />
        <Label className="text-sm cursor-pointer" onClick={() => set({ per_segment: !rule.per_segment })}>
          Apply <strong>per segment</strong> (multiply by number of segments in the journey)
        </Label>
      </div>
    </div>
  );
}
